import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';
import { ManageDuplicatesService } from '../../services/manage-duplicates.service';
import {
  DuplicateIdentityGroup,
  DuplicateListResponse,
  DuplicateSummaryMetrics,
} from '../../models/duplicate-business';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ManageDuplicateItemsComponent } from '../manage-duplicate-items/manage-duplicate-items.component';

@Component({
  selector: 'app-manage-duplicates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationComponent,
    PageSizeComponent,
    RecordDetailsComponent,
    SearchInputComponent,
    ManageDuplicateItemsComponent,
  ],
  templateUrl: './manage-duplicates.component.html',
  styleUrl: './manage-duplicates.component.scss',
})
export class ManageDuplicatesComponent
  extends MenuComponentBase
  implements OnInit
{
  private duplicateService = inject(ManageDuplicatesService);
  private searchSubject = new Subject<string>();

  identities: DuplicateIdentityGroup[] = [];
  summary: DuplicateSummaryMetrics = {
    email_duplicate_groups: 0,
    phone_duplicate_groups: 0,
    name_duplicate_groups: 0,
  };
  total = 0;
  page = 1;
  pageSize = 10;
  search = '';
  identityTypeFilter = 'email';

  isLoading = false;
  isSummaryLoading = false;
  expandedIdentityId: string | null = null;

  // Shared Record Details Popup State
  showRecordDetails = false;
  selectedUniqueId = '';

  override ngOnInit(): void {
    super.ngOnInit();
    this.setupSearchSubscription();
    this.loadSummary();
    this.loadDuplicates();
  }

  private setupSearchSubscription(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query: string) => {
        this.search = query;
        this.page = 1;
        this.loadDuplicates();
      });
  }

  loadSummary(): void {
    this.isSummaryLoading = true;
    this.duplicateService
      .getDuplicateSummary()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (summary: DuplicateSummaryMetrics) => {
          this.summary = summary;
          this.isSummaryLoading = false;
        },
        error: (err: unknown) => {
          console.error('Error loading duplicate summary metrics', err);
          this.isSummaryLoading = false;
        },
      });
  }

  loadDuplicates(): void {
    this.isLoading = true;
    this.duplicateService
      .getDuplicates(
        this.page,
        this.pageSize,
        this.search,
        this.identityTypeFilter,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: DuplicateListResponse) => {
          this.identities = res.identities.map((item) => ({
            ...item,
            records: item.records || [],
            isLoadingMembers: false,
            isLoadingMore: false,
            membersPage: 1,
            membersPageSize: 5,
            membersTotal: item.match_count || 0,
            hasMoreMembers: false,
          }));
          this.total = res.total;
          this.isLoading = false;
        },
        error: (err: unknown) => {
          console.error('Error loading duplicates', err);
          this.isLoading = false;
        },
      });
  }

  onSearchChange(): void {
    this.searchSubject.next(this.search);
  }

  onSearchQueryChange(query: string): void {
    this.searchSubject.next(query);
  }

  onCategoryTabChange(category: string): void {
    this.identityTypeFilter = category;
    this.page = 1;
    this.loadDuplicates();
  }

  onPageChange(newPage: number): void {
    this.page = newPage;
    this.loadDuplicates();
  }

  onPageSizeChange(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.page = 1;
    this.loadDuplicates();
  }

  toggleExpand(group: DuplicateIdentityGroup): void {
    this.expandedIdentityId =
      this.expandedIdentityId === group.id ? null : group.id;
  }

  viewRecordDetails(uniqueId: string): void {
    this.selectedUniqueId = uniqueId;
    this.showRecordDetails = true;
  }
}
